import { Alignment, Resolution } from 'src/domains/calculators/utilities/types';
import { CHART_LABEL_TEXT_SIZE } from './drawing/constants';
import { Coordinates, CoordinatesSet, FramingChartCoordinates } from './types';

// Constants
const COORDINATE_CONSTANTS = {
  CENTER_CROSS: {
    SPACING_RATIO: 0.2,
  },
  TEST_PATTERN: {
    PADDING_RATIO: 0.05,
    POSITIONS: {
      LEFT_EDGE: 1 / 9,
      RIGHT_EDGE: 8 / 9,
    },
  },
  TEXT_POSITION_RATIO: 0.65,
};

interface CoordinateCalculationInputs {
  alignment: Alignment;
  effectiveCanvas: Resolution | null;
  canvasResolution: Resolution;
  framingDecisionProtection: Resolution;
  framingDecision: Resolution;
  testPatternResolution: Resolution;
  centerCrossResolution: Resolution;
  logoResolution: Resolution;
  protection: number;
  squeeze: number;
  desqueezed?: boolean;
}

/**
 * Calculate all coordinate positions for the framing chart
 */
export function calculateAllCoordinates(
  inputs: CoordinateCalculationInputs
): FramingChartCoordinates {
  const {
    alignment,
    effectiveCanvas,
    canvasResolution,
    framingDecisionProtection,
    framingDecision,
    testPatternResolution,
    centerCrossResolution,
    logoResolution,
    protection,
    squeeze,
    desqueezed,
  } = inputs;

  const canvasCoordinates = { x: 0, y: 0 };
  const effectiveSqueeze = desqueezed ? 1 : squeeze;

  const effectiveCanvasCoordinates = effectiveCanvas
    ? getElementCoordinatesInContainer(
        canvasResolution,
        canvasCoordinates,
        effectiveCanvas
      )
    : undefined;

  const framingDecisionProtectionCoordinates = getElementCoordinatesInContainer(
    effectiveCanvas ?? canvasResolution,
    effectiveCanvasCoordinates ?? canvasCoordinates,
    framingDecisionProtection,
    alignment
  );

  const framingDecisionCoordinates = getElementCoordinatesInContainer(
    framingDecisionProtection,
    framingDecisionProtectionCoordinates,
    framingDecision
  );

  const textCoordinates = getCentralTextCoordinates(
    framingDecision,
    framingDecisionCoordinates,
    effectiveSqueeze
  );

  const testPatternCoordinates = getTestPatternCoordinates(
    framingDecisionCoordinates,
    framingDecision,
    testPatternResolution
  );

  const centerCrossCoordinates = getCrossCoordinates(
    framingDecision,
    framingDecisionCoordinates
  );

  const logoCoordinates = getLogoCoordinates(
    framingDecision,
    framingDecisionCoordinates,
    centerCrossResolution,
    centerCrossCoordinates,
    logoResolution
  );

  const canvasDimensionsCoordinates = getCanvasDimensionsCoordinates(
    framingDecision,
    framingDecisionCoordinates
  );

  const effectiveCanvasDimensionsCoordinates =
    effectiveCanvas && effectiveCanvasCoordinates
      ? getEffectiveCanvasDimensionsCoordinates(
          effectiveCanvasCoordinates,
          effectiveCanvas
        )
      : undefined;

  const framingDecisionProtectionDimensionsCoordinates =
    protection > 0
      ? getFramingDecisionProtectionDimensionsCoordinates(
          framingDecisionProtection,
          framingDecisionProtectionCoordinates
        )
      : undefined;

  const framingDecisionDimensionsCoordinates =
    getFramingDecisionDimensionsCoordinates(
      framingDecision,
      framingDecisionCoordinates
    );

  const canvasPixelCoordinates = getCanvasPixelCoordinates(
    canvasResolution,
    framingDecision,
    framingDecisionCoordinates
  );

  const effectiveCanvasPixelCoordinates =
    effectiveCanvas && effectiveCanvasCoordinates
      ? getEffectiveCanvasPixelCoordinates(
          effectiveCanvas,
          effectiveCanvasCoordinates
        )
      : undefined;

  const protectionPixelCoordinates =
    protection > 0
      ? getProtectionPixelCoordinates(
          framingDecisionProtection,
          framingDecisionProtectionCoordinates
        )
      : undefined;

  const framingDecisionPixelCoordinates = getFramingDecisionPixelCoordinates(
    framingDecision,
    framingDecisionCoordinates
  );

  const anchorPointPixelCoordinates = getAnchorPointPixelCoordinates(
    framingDecisionCoordinates
  );

  return {
    canvasCoordinates,
    effectiveCanvasCoordinates,
    framingDecisionProtectionCoordinates,
    framingDecisionCoordinates,
    textCoordinates,
    testPatternCoordinates,
    centerCrossCoordinates,
    logoCoordinates,
    canvasDimensionsCoordinates,
    effectiveCanvasDimensionsCoordinates,
    framingDecisionProtectionDimensionsCoordinates,
    framingDecisionDimensionsCoordinates,
    canvasPixelCoordinates,
    effectiveCanvasPixelCoordinates,
    framingDecisionPixelCoordinates,
    protectionPixelCoordinates,
    anchorPointPixelCoordinates,
  };
}

function getTestPatternCoordinates(
  containerCoordinates: Coordinates,
  containerResolution: Resolution,
  testPatternResolution: Resolution
): Coordinates[] {
  const { TEST_PATTERN } = COORDINATE_CONSTANTS;
  const { height: testPatternHeight } = testPatternResolution;

  const padding = containerResolution.height * TEST_PATTERN.PADDING_RATIO;

  // Define test pattern positions
  return [
    // Top left
    {
      x:
        containerCoordinates.x +
        containerResolution.width * TEST_PATTERN.POSITIONS.LEFT_EDGE,
      y: containerCoordinates.y + testPatternHeight + padding,
    },
    // Bottom left
    {
      x:
        containerCoordinates.x +
        containerResolution.width * TEST_PATTERN.POSITIONS.LEFT_EDGE,
      y:
        containerCoordinates.y +
        containerResolution.height -
        testPatternHeight -
        padding,
    },
    // Top right
    {
      x:
        containerCoordinates.x +
        containerResolution.width * TEST_PATTERN.POSITIONS.RIGHT_EDGE,
      y: containerCoordinates.y + testPatternHeight + padding,
    },
    // Bottom right
    {
      x:
        containerCoordinates.x +
        containerResolution.width * TEST_PATTERN.POSITIONS.RIGHT_EDGE,
      y:
        containerCoordinates.y +
        containerResolution.height -
        testPatternHeight -
        padding,
    },
  ];
}

function getCentralTextCoordinates(
  containerResolution: Resolution,
  containerCoordinates: Coordinates,
  squeeze: number
): Coordinates {
  const textX =
    (containerResolution.width / 2 + containerCoordinates.x) * squeeze;

  const textY =
    containerResolution.height * COORDINATE_CONSTANTS.TEXT_POSITION_RATIO +
    containerCoordinates.y;

  return {
    x: textX,
    y: textY,
  };
}

function getCrossCoordinates(
  containerResolution: Resolution,
  containerCoordinates: Coordinates
): Coordinates {
  const x = containerResolution.width / 2 + containerCoordinates.x;
  const y = containerResolution.height / 2 + containerCoordinates.y;

  return { x, y };
}

function getLogoCoordinates(
  containerResolution: Resolution,
  containerCoordinates: Coordinates,
  crossResolution: Resolution,
  crossCoordinates: Coordinates,
  logoResolution: Resolution
): Coordinates {
  const { width: containerWidth } = containerResolution;
  const { x: containerX } = containerCoordinates;

  const { width, height } = logoResolution;

  // Center the logo horizontally
  const x = (containerWidth - width) / 2 + containerX;

  // Position the logo above the cross
  const { height: crossHeight } = crossResolution;
  const logoSpacing =
    crossHeight * COORDINATE_CONSTANTS.CENTER_CROSS.SPACING_RATIO;

  const { y: crossY } = crossCoordinates;
  const y = crossY - height - logoSpacing;

  return {
    x,
    y,
  };
}

export function getElementCoordinatesInContainer(
  containerResolution: Resolution,
  containerCoordinates: Coordinates,
  elementResolution: Resolution,
  alignment: Alignment = 'center'
): Coordinates {
  const x =
    (containerResolution.width - elementResolution.width) / 2 +
    containerCoordinates.x;
  let y =
    (containerResolution.height - elementResolution.height) / 2 +
    containerCoordinates.y; // Center alignment

  if (alignment === 'top') {
    y = containerCoordinates.y;
  } else if (alignment === 'bottom') {
    y =
      containerResolution.height +
      containerCoordinates.y -
      elementResolution.height;
  }

  return { x, y };
}

const TOP_TEXT_OFFSET = CHART_LABEL_TEXT_SIZE - 5;
const BOTTOM_TEXT_OFFSET = 5;

function getCanvasDimensionsCoordinates(
  framingDecisionResolution: Resolution,
  framingDecisionCoordinates: Coordinates
): Coordinates {
  // Position canvas dimensions at the top of the canvas on the left side
  // We use the framing decision size and coordinates to determine the horizontal position
  // for optimal placement of the text when the framing decision is a similar size as the canvas
  const x =
    framingDecisionCoordinates.x + framingDecisionResolution.width * 0.125;
  const y = TOP_TEXT_OFFSET;

  return { x, y };
}

function getEffectiveCanvasDimensionsCoordinates(
  effectiveCanvasCoordinates: Coordinates,
  effectiveCanvasResolution: Resolution
): Coordinates {
  // Position effective canvas dimensions above the protection left pixel label but below left-central triangles
  const x = effectiveCanvasCoordinates.x + TOP_TEXT_OFFSET;
  const y =
    effectiveCanvasCoordinates.y + effectiveCanvasResolution.height * 0.75;

  return { x, y };
}

function getFramingDecisionProtectionDimensionsCoordinates(
  framingDecisionProtectionResolution: Resolution,
  framingDecisionProtectionCoordinates: Coordinates
): Coordinates {
  // Position protection dimensions text at the bottom on the right side of the protection area
  const x =
    framingDecisionProtectionCoordinates.x +
    framingDecisionProtectionResolution.width * 0.55;
  const y =
    framingDecisionProtectionCoordinates.y +
    framingDecisionProtectionResolution.height -
    BOTTOM_TEXT_OFFSET;

  return { x, y };
}

function getFramingDecisionDimensionsCoordinates(
  framingDecisionResolution: Resolution,
  framingDecisionCoordinates: Coordinates
): Coordinates {
  // Position framing decision dimensions on the top of the framing decision on the right side
  const x =
    framingDecisionCoordinates.x + framingDecisionResolution.width * 0.55;
  const y = framingDecisionCoordinates.y + TOP_TEXT_OFFSET;

  return { x, y };
}

function getCanvasPixelCoordinates(
  canvasResolution: Resolution,
  framingDecisionResolution: Resolution,
  framingDecisionCoordinates: Coordinates
): CoordinatesSet {
  const horizontalPixelsY =
    framingDecisionCoordinates.y + framingDecisionResolution.height * 0.225;

  const left = {
    x: TOP_TEXT_OFFSET,
    y: horizontalPixelsY,
  };

  const right = {
    x: canvasResolution.width - BOTTOM_TEXT_OFFSET,
    y: horizontalPixelsY,
  };

  const verticalPixelsX =
    framingDecisionCoordinates.x + framingDecisionResolution.width * 0.25;
  const top = {
    x: verticalPixelsX,
    y: TOP_TEXT_OFFSET,
  };

  const bottom = {
    x: verticalPixelsX,
    y: canvasResolution.height - BOTTOM_TEXT_OFFSET,
  };

  return { left, right, top, bottom };
}

function getEffectiveCanvasPixelCoordinates(
  effectiveCanvasResolution: Resolution,
  effectiveCanvasCoordinates: Coordinates
): CoordinatesSet {
  const horizontalPixelsY =
    effectiveCanvasCoordinates.y + effectiveCanvasResolution.height * 0.45;
  const left = {
    x: effectiveCanvasCoordinates.x + TOP_TEXT_OFFSET,
    y: horizontalPixelsY,
  };

  const right = {
    x:
      effectiveCanvasCoordinates.x +
      effectiveCanvasResolution.width -
      BOTTOM_TEXT_OFFSET,
    y: horizontalPixelsY,
  };

  const verticalPixelsX =
    effectiveCanvasCoordinates.x + effectiveCanvasResolution.width * 0.375;

  const top = {
    x: verticalPixelsX,
    y: effectiveCanvasCoordinates.y + TOP_TEXT_OFFSET,
  };

  const bottom = {
    x: verticalPixelsX,
    y:
      effectiveCanvasCoordinates.y +
      effectiveCanvasResolution.height -
      BOTTOM_TEXT_OFFSET,
  };

  return { left, right, top, bottom };
}

function getProtectionPixelCoordinates(
  protectionResolution: Resolution,
  protectionCoordinates: Coordinates
): CoordinatesSet {
  const y = protectionCoordinates.y + protectionResolution.height * 0.85;

  const left = {
    x: protectionCoordinates.x + TOP_TEXT_OFFSET,
    y,
  };

  const right = {
    x:
      protectionCoordinates.x + protectionResolution.width - BOTTOM_TEXT_OFFSET,
    y,
  };

  const verticalPixelsX =
    protectionCoordinates.x + protectionResolution.width * 0.8;

  const top = {
    x: verticalPixelsX,
    y: protectionCoordinates.y + TOP_TEXT_OFFSET,
  };
  const bottom = {
    x: verticalPixelsX,
    y:
      protectionCoordinates.y +
      protectionResolution.height -
      BOTTOM_TEXT_OFFSET,
  };

  return { left, right, top, bottom };
}

function getFramingDecisionPixelCoordinates(
  framingDecisionResolution: Resolution,
  framingDecisionCoordinates: Coordinates
): { right: Coordinates; bottom: Coordinates } {
  const right = {
    x:
      framingDecisionCoordinates.x +
      framingDecisionResolution.width -
      BOTTOM_TEXT_OFFSET,
    y: framingDecisionCoordinates.y + framingDecisionResolution.height * 0.85,
  };

  const bottom = {
    x: framingDecisionCoordinates.x + framingDecisionResolution.width * 0.15,
    y:
      framingDecisionCoordinates.y +
      framingDecisionResolution.height -
      BOTTOM_TEXT_OFFSET,
  };

  return { right, bottom };
}

function getAnchorPointPixelCoordinates(
  framingDecisionCoordinates: Coordinates
): Coordinates {
  const x = framingDecisionCoordinates.x + BOTTOM_TEXT_OFFSET;
  const y = framingDecisionCoordinates.y + TOP_TEXT_OFFSET;

  return { x, y };
}
